import { BolsoLayout } from "@/components/BolsoLayout";
import { TransactionForm } from "@/components/TransactionForm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { Edit2, Plus, Search, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";

type TxRow = {
  id: number;
  type: "income" | "expense" | "transfer";
  amount: string | number;
  description?: string | null;
  date: Date | string;
  categoryId?: number | null;
  notes?: string | null;
  aiCategorized?: boolean | null;
  userId: number;
};

export default function Transactions() {
  const { t, ui, formatCurrency, formatDate, categoryName } = useI18n();
  const [showForm, setShowForm] = useState(false);
  const [editTx, setEditTx] = useState<TxRow | null>(null);
  const [deleteTx, setDeleteTx] = useState<TxRow | null>(null);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");

  const { data: transactions = [], isLoading } = trpc.transactions.list.useQuery({ limit: 100 });
  const { data: categories = [] } = trpc.categories.list.useQuery({});
  const utils = trpc.useUtils();

  const deleteMutation = trpc.transactions.delete.useMutation({
    onSuccess: () => {
      toast.success(ui.transactions.deleted);
      utils.transactions.list.invalidate();
      utils.transactions.monthSummary.invalidate();
      utils.transactions.categorySpending.invalidate();
      utils.transactions.monthlyTotals.invalidate();
    },
    onError: () => toast.error(ui.transactions.deleteError),
  });

  const filtered = transactions.filter((tx) => {
    const matchSearch = !search ||
      tx.description?.toLowerCase().includes(search.toLowerCase()) ||
      categoryName(categories.find((c) => c.id === tx.categoryId)?.name).toLowerCase().includes(search.toLowerCase());
    const matchType = typeFilter === "all" || tx.type === typeFilter;
    const matchCat = categoryFilter === "all" || String(tx.categoryId) === categoryFilter;
    return matchSearch && matchType && matchCat;
  });

  return (
    <BolsoLayout onNewTransaction={() => setShowForm(true)}>
      <div className="p-4 lg:p-6 max-w-4xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-serif font-bold text-foreground">{t.transactions.title}</h1>
          <Button onClick={() => setShowForm(true)} size="sm" className="gap-1.5">
            <Plus className="w-4 h-4" /> {t.transactions.new.split(" ")[0]}
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t.transactions.search}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full sm:w-36">
              <SelectValue placeholder={ui.transactions.typePlaceholder} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.transactions.all}</SelectItem>
              <SelectItem value="income">{t.transactions.income}</SelectItem>
              <SelectItem value="expense">{t.transactions.expense}</SelectItem>
              <SelectItem value="transfer">{t.transactions.transfer}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-full sm:w-40">
              <SelectValue placeholder={ui.transactions.categoryPlaceholder} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.transactions.all}</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={String(c.id)}>{categoryName(c.name)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* List */}
        <Card className="border-border shadow-sm">
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-4 space-y-3">
                {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-14" />)}
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-10 text-center text-muted-foreground">
                <p className="text-4xl mb-3">💸</p>
                <p className="font-medium">{t.transactions.noTransactions}</p>
                <p className="text-sm mt-1">{t.transactions.addTransaction}</p>
                <Button variant="outline" size="sm" className="mt-4" onClick={() => setShowForm(true)}>
                  <Plus className="w-4 h-4 mr-1" /> {t.common.add}
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {filtered.map((tx) => {
                  const cat = categories.find((c) => c.id === tx.categoryId);
                  return (
                    <div key={tx.id} className="flex items-center gap-3 px-4 py-3.5 hover:bg-muted/30 transition-colors group">
                      <div className={cn(
                        "w-10 h-10 rounded-xl flex items-center justify-center text-base flex-shrink-0 font-semibold",
                        tx.type === "income" ? "income-badge" : tx.type === "transfer" ? "bg-blue-100 text-blue-600" : "expense-badge"
                      )}>
                        {tx.type === "income" ? "↑" : tx.type === "transfer" ? "↔" : "↓"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {tx.description || categoryName(cat?.name) || ui.common.noDescription}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <p className="text-xs text-muted-foreground">
                            {formatDate(tx.date, { day: "2-digit", month: "2-digit", year: "numeric" })}
                          </p>
                          {cat && (
                            <Badge variant="secondary" className="text-xs px-1.5 py-0 h-4">
                              {categoryName(cat.name)}
                            </Badge>
                          )}
                          {tx.aiCategorized && (
                            <Badge variant="outline" className="text-xs px-1.5 py-0 h-4 text-primary border-primary/30">
                              IA
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className={cn(
                          "text-sm font-semibold tabular-nums mr-1",
                          tx.type === "income" ? "income-text" : tx.type === "transfer" ? "text-blue-600" : "expense-text"
                        )}>
                          {tx.type === "income" ? "+" : tx.type === "transfer" ? "↔" : "-"}{formatCurrency(parseFloat(String(tx.amount)))}
                        </span>
                        <button
                          onClick={() => setEditTx(tx as TxRow)}
                          className="sm:opacity-0 sm:group-hover:opacity-100 transition-opacity p-2 sm:p-1 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary"
                          title={t.transactions.edit}
                          aria-label={t.transactions.edit}
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setDeleteTx(tx as TxRow)}
                          className="sm:opacity-0 sm:group-hover:opacity-100 transition-opacity p-2 sm:p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                          title={t.transactions.delete}
                          aria-label={t.transactions.delete}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Totalizador */}
            {!isLoading && filtered.length > 0 && (() => {
              const totalIncome = filtered.filter(t => t.type === "income").reduce((s, t) => s + parseFloat(String(t.amount)), 0);
              const totalExpense = filtered.filter(t => t.type === "expense").reduce((s, t) => s + parseFloat(String(t.amount)), 0);
              const totalTransfer = filtered.filter(t => t.type === "transfer").reduce((s, t) => s + parseFloat(String(t.amount)), 0);
              const hasMultipleTypes = [totalIncome, totalExpense, totalTransfer].filter(v => v > 0).length > 1;
              return (
                <div className="px-4 py-3 border-t border-border bg-muted/20 flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs text-muted-foreground">
                    {filtered.length} {filtered.length === 1 ? ui.common.transaction : ui.common.transactions}
                  </span>
                  <div className="flex flex-wrap gap-3 text-sm font-semibold tabular-nums">
                    {totalIncome > 0 && (
                      <span className="income-text">+{formatCurrency(totalIncome)}</span>
                    )}
                    {totalExpense > 0 && (
                      <span className="expense-text">-{formatCurrency(totalExpense)}</span>
                    )}
                    {totalTransfer > 0 && (
                      <span className="text-blue-600">↔{formatCurrency(totalTransfer)}</span>
                    )}
                    {hasMultipleTypes && (
                      <span className={cn("text-foreground border-l border-border pl-3", (totalIncome - totalExpense) >= 0 ? "income-text" : "expense-text")}>
                        {ui.common.balance}: {formatCurrency(totalIncome - totalExpense)}
                      </span>
                    )}
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      </div>

      {/* Formulário de novo lançamento */}
      <TransactionForm
        open={showForm}
        onClose={() => setShowForm(false)}
        onSuccess={() => setShowForm(false)}
      />

      {/* Formulário de edição */}
      <TransactionForm
        open={!!editTx}
        onClose={() => setEditTx(null)}
        editTransaction={editTx ?? undefined}
        onSuccess={() => setEditTx(null)}
      />

      <AlertDialog open={!!deleteTx} onOpenChange={(open) => !open && setDeleteTx(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.transactions.confirmDelete}</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTx?.description || ui.common.noDescription} — {deleteTx ? formatCurrency(parseFloat(String(deleteTx.amount))) : ""}. {t.transactions.confirmDeleteDesc}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t.common.cancel}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deleteTx) deleteMutation.mutate({ id: deleteTx.id });
                setDeleteTx(null);
              }}
            >
              {t.common.delete}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </BolsoLayout>
  );
}
